<template>
  <div class="na-page na-staff">
    <StaffHeader role="Admin" />

    <main class="st-wrap st-below">
      <div class="st-head">
        <div>
          <span class="st-kicker">Catalog</span>
          <h1 class="st-title">Products &amp; <em>add-ons</em></h1>
          <p class="st-sub">Everything you offer beyond the three plans — extra training, a second app, custom design work, more email.</p>
        </div>
        <div class="head-actions">
          <label class="st-search"><v-icon size="18">mdi-magnify</v-icon><input v-model="search" placeholder="Search products" /></label>
          <button type="button" class="st-btn primary" @click="openCreate"><v-icon size="18">mdi-plus</v-icon>New product</button>
        </div>
      </div>

      <div class="st-seg filter">
        <button v-for="s in ['all', ...STATUSES]" :key="s" type="button" :class="{ on: statusFilter === s }" @click="statusFilter = s">{{ s === 'all' ? `All (${products.length})` : `${cap(s)} (${products.filter(p => p.status === s).length})` }}</button>
      </div>

      <section class="st-card">
        <div v-if="loading" class="state"><div class="st-loader"></div><p>Loading products…</p></div>
        <div v-else-if="!filtered.length" class="st-empty">
          <v-icon size="40">mdi-tag-plus-outline</v-icon>
          <h3>{{ products.length ? 'Nothing matches' : 'No products yet' }}</h3>
          <p v-if="!products.length">Add your first add-on — for example "Extra staff training session" or "Second app for the school district".</p>
          <button v-if="!products.length" type="button" class="st-btn primary" @click="openCreate"><v-icon size="18">mdi-plus</v-icon>New product</button>
        </div>
        <div v-else class="st-table">
          <div class="st-tr st-th p-row"><span>Product</span><span>Type</span><span>Price</span><span>Status</span><span>Updated</span><span></span></div>
          <div v-for="p in filtered" :key="p.id" class="st-tr p-row click" @click="openRow(p)">
            <span class="name"><span class="st-avatar">{{ initials(p.name) }}</span><span class="nm"><b>{{ p.name }}</b><small>{{ p.summary }}</small></span></span>
            <span><span class="st-badge navy">{{ p.kind || 'Add-on' }}</span></span>
            <span>{{ p.price || '—' }}</span>
            <span><span class="st-badge" :class="{ active: 'green', paused: 'brass', archived: '' }[p.status]">{{ p.status }}</span></span>
            <span class="st-muted"><small>{{ since(p.updatedAt) }}</small></span>
            <span class="acts" @click.stop>
              <button v-if="hasWorkspace" type="button" class="st-icon-btn" title="Open workspace" @click="openWorkspace(p)"><v-icon size="18">mdi-open-in-app</v-icon></button>
              <button type="button" class="st-icon-btn" title="Edit" @click="edit(p)"><v-icon size="18">mdi-pencil-outline</v-icon></button>
              <button type="button" class="st-icon-btn danger" title="Delete" :disabled="deletingId === p.id" @click="confirmDelete = p"><v-icon size="18">mdi-delete-outline</v-icon></button>
            </span>
          </div>
        </div>
      </section>
    </main>

    <!-- Create / edit -->
    <div v-if="dlg" class="st-overlay" @click.self="dlg = false">
      <div class="st-modal">
        <div class="st-modal-head"><h3>{{ form.id ? 'Edit product' : 'New product' }}</h3><code>{{ form.slug || slugify(form.name) || 'slug' }}</code></div>
        <div class="st-modal-body st-stack">
          <div class="st-field"><label>Name *</label><input v-model="form.name" placeholder="Extra staff training session" @input="autoSlug" /></div>
          <div class="st-grid-2">
            <div class="st-field"><label>Type</label><select v-model="form.kind"><option v-for="k in KINDS" :key="k">{{ k }}</option></select></div>
            <div class="st-field"><label>Price (as you'd quote it)</label><input v-model="form.price" placeholder="$250 per session" /></div>
          </div>
          <div class="st-grid-2">
            <div class="st-field"><label>Slug (unique)</label><input v-model="form.slug" placeholder="extra-training" /></div>
            <div class="st-field"><label>Status</label><select v-model="form.status"><option v-for="s in STATUSES" :key="s" :value="s">{{ cap(s) }}</option></select></div>
          </div>
          <div class="st-field"><label>Summary</label><textarea v-model="form.summary" rows="4" placeholder="What's included and who it's for."></textarea></div>
          <div v-if="error" class="st-alert">{{ error }}</div>
        </div>
        <div class="st-modal-foot">
          <button type="button" class="st-btn ghost" @click="dlg = false">Cancel</button>
          <button type="button" class="st-btn primary" :disabled="saving || !form.name.trim()" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- Delete -->
    <div v-if="confirmDelete" class="st-overlay" @click.self="confirmDelete = null">
      <div class="st-modal">
        <div class="st-modal-head"><h3>Delete "{{ confirmDelete.name }}"?</h3></div>
        <div class="st-modal-body"><p>This can't be undone. To hide it without deleting, set its status to Archived instead.</p></div>
        <div class="st-modal-foot">
          <button type="button" class="st-btn ghost" @click="confirmDelete = null">Keep it</button>
          <button type="button" class="st-btn danger" :disabled="!!deletingId" @click="removeItem(confirmDelete)">Delete</button>
        </div>
      </div>
    </div>

    <div v-if="toast" class="st-toast">{{ toast }}<button type="button" @click="toast = ''">✕</button></div>
  </div>
</template>

<script>
import StaffHeader from '@/components/StaffHeader.vue'
import { db } from '@/firebase'
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore'
import { fnError } from '@/billingConfig'
import '@/assets/na-staff.css'

const STATUSES = ['active', 'paused', 'archived']
const KINDS = ['Add-on', 'Service', 'Training', 'Second app', 'Custom work']
const blank = () => ({ id: '', name: '', slug: '', summary: '', status: 'active', kind: 'Add-on', price: '' })

export default {
  name: 'AdminProducts',
  components: { StaffHeader },
  data: () => ({ STATUSES, KINDS, loading: true, products: [], unsub: null, search: '', statusFilter: 'all', dlg: false, saving: false, error: '', deletingId: '', confirmDelete: null, form: blank(), toast: '' }),
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase()
      return this.products
        .filter(p => this.statusFilter === 'all' || p.status === this.statusFilter)
        .filter(p => !q || [p.name, p.summary, p.slug, p.kind, p.price].join(' ').toLowerCase().includes(q))
    },
    // Only show the workspace button if a product detail page exists in the router.
    hasWorkspace() { return this.$router.resolve('/admin/products/x').route.matched.length > 0 },
  },
  created() {
    this.unsub = onSnapshot(query(collection(db, 'products'), orderBy('updatedAt', 'desc')), s => {
      this.products = s.docs.map(d => ({ id: d.id, ...d.data() }))
      this.loading = false
    }, e => { console.error(e); this.loading = false; this.toast = 'Could not load products: ' + fnError(e) })
  },
  beforeDestroy() { if (this.unsub) this.unsub() },
  methods: {
    cap(s) { return s.charAt(0).toUpperCase() + s.slice(1) },
    initials(n = '') { return String(n).split(/\s+/).map(x => x[0]).join('').slice(0, 2).toUpperCase() || '?' },
    since(ts) {
      const d = ts && (ts.toDate ? ts.toDate() : ts.seconds ? new Date(ts.seconds * 1000) : null)
      if (!d) return '—'
      const s = Math.max(1, Math.floor((Date.now() - d) / 1000))
      for (const [l, n] of [['y', 31536000], ['mo', 2592000], ['d', 86400], ['h', 3600], ['m', 60]]) if (s >= n) return `${Math.floor(s / n)}${l} ago`
      return 'just now'
    },
    slugify(s = '') { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 64) },
    autoSlug() { if (!this.form.id) this.form.slug = this.slugify(this.form.name) },
    openCreate() { this.form = blank(); this.error = ''; this.dlg = true },
    edit(p) { this.form = { ...blank(), ...p }; this.error = ''; this.dlg = true },
    openRow(p) { if (this.hasWorkspace) this.openWorkspace(p); else this.edit(p) },
    openWorkspace(p) { this.$router.push(`/admin/products/${p.id}`).catch(() => {}) },
    async save() {
      const slug = this.form.slug || this.slugify(this.form.name)
      if (this.products.some(p => p.slug === slug && p.id !== this.form.id)) { this.error = 'Another product already uses that slug.'; return }
      this.saving = true
      try {
        const payload = { name: this.form.name.trim(), slug, summary: this.form.summary.trim(), status: this.form.status, kind: this.form.kind, price: this.form.price.trim(), visibility: 'team', updatedAt: serverTimestamp() }
        if (this.form.id) await updateDoc(doc(db, 'products', this.form.id), payload)
        else await addDoc(collection(db, 'products'), { ...payload, createdAt: serverTimestamp() })
        this.dlg = false
        this.toast = 'Saved'
        setTimeout(() => { this.toast = '' }, 2500)
      } catch (e) { this.error = 'Could not save: ' + fnError(e) } finally { this.saving = false }
    },
    async removeItem(p) {
      this.deletingId = p.id
      try { await deleteDoc(doc(db, 'products', p.id)); this.confirmDelete = null } catch (e) { this.toast = 'Could not delete: ' + fnError(e) } finally { this.deletingId = '' }
    },
  },
}
</script>

<style scoped>
.head-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.filter { margin-bottom: 18px; }
.state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 50px; color: var(--na-ink-2); }
.p-row { grid-template-columns: 2.4fr 0.9fr 1fr 0.8fr 0.8fr 130px; }
.click { cursor: pointer; }
.click:hover { background: #FBF8F1; }
.name { display: flex; gap: 12px; align-items: center; min-width: 0; }
.name .st-avatar { width: 38px; height: 38px; font-size: 0.8rem; }
.nm { display: flex; flex-direction: column; min-width: 0; }
.nm small { color: var(--na-ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.acts { display: flex; gap: 6px; justify-content: flex-end; }
.st-modal-head code { font-size: 0.8rem; }
@media (max-width: 960px) {
  .p-row { grid-template-columns: 1fr 1fr; }
  .p-row.st-th { display: none; }
}
</style>